import { useGetUserRatings } from "@/api/admin/useGetUserRatings";
import { AdminUserRatingItemDto, UserRatingValue } from "@/api/generated/generatedTypes";
import { USER_RATING_LABELS } from "@/api/user-rating/dto";
import { Badge, Loader, Stack, Table, Text, Title } from "@mantine/core";
import dayjs from "dayjs";

const RATING_COLORS: Record<UserRatingValue, string> = {
  [UserRatingValue.LIKED]: "green",
  [UserRatingValue.DISLIKED]: "red",
  [UserRatingValue.NOT_SURE]: "gray",
};

interface UserRatingsSectionProps {
  userId: string;
}

export const UserRatingsSection = ({ userId }: UserRatingsSectionProps) => {
  const { data, isLoading } = useGetUserRatings(userId, true);

  if (isLoading) {
    return <Loader size="sm" />;
  }

  return (
    <Stack gap="md">
      <RatingsTable
        title="이 유저가 남긴 평가"
        emptyMessage="남긴 평가가 없습니다"
        ratings={data?.given ?? []}
      />
      <RatingsTable
        title="이 유저가 받은 평가"
        emptyMessage="받은 평가가 없습니다"
        ratings={data?.received ?? []}
      />
    </Stack>
  );
};

const RatingsTable = ({
  title,
  emptyMessage,
  ratings,
}: {
  title: string;
  emptyMessage: string;
  ratings: AdminUserRatingItemDto[];
}) => {
  return (
    <Stack gap="xs">
      <Title order={6} c="black">
        {title}
      </Title>
      {ratings.length === 0 ? (
        <Text c="dimmed" size="sm">
          {emptyMessage}
        </Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ color: "black" }}>날짜</Table.Th>
              <Table.Th style={{ color: "black" }}>상대</Table.Th>
              <Table.Th style={{ color: "black" }}>평가</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ratings.map((rating) => (
              <Table.Tr key={`${rating.eventId}-${rating.counterpartUserId}`}>
                <Table.Td style={{ color: "black" }}>
                  {dayjs(rating.eventStartTime).format("YY.MM.DD")}
                </Table.Td>
                <Table.Td style={{ color: "black" }}>{rating.counterpartDisplayName}</Table.Td>
                <Table.Td>
                  <Badge color={RATING_COLORS[rating.value]} variant="light" size="sm">
                    {USER_RATING_LABELS[rating.value]}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
};
